// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  Mail, Lock, Eye, EyeOff, Leaf, AlertCircle, CheckCircle,
  ArrowLeft, Send, X
} from 'lucide-react';
import SuccessModal from '../components/SuccessModal';

const Login = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [siteLogo, setSiteLogo] = useState('');
  const [siteName, setSiteName] = useState('NAVIROSE');

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch site settings (logo + name)
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const docRef = doc(db, 'settings', 'site');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.logo) setSiteLogo(data.logo);
          if (data.siteName) setSiteName(data.siteName);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  React.useEffect(() => {
    if (user) navigate('/profile');
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Email aur password dono daalein.');
      return;
    }

    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate('/profile');
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Email ya password galat hai.');
      } else if (err.code === 'auth/user-not-found') {
        setError('Is email se koi account nahi mila.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Bohat zyada attempts. Thodi der baad try karein.');
      } else {
        setError('Login nahi ho saka. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const googleUser = result.user;

      const userRef = doc(db, 'users', googleUser.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        await setDoc(userRef, {
          name: googleUser.displayName || 'Customer',
          email: googleUser.email,
          phone: '',
          role: 'customer',
          photoURL: googleUser.photoURL || '',
          createdAt: new Date(),
        });
      }

      navigate('/profile');
    } catch (err) {
      console.error('Google login error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Google popup band ho gaya. Dobara try karein.');
      } else {
        setError('Google sign-in nahi ho saka.');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');

    if (!forgotEmail) {
      setForgotError('Email daalein.');
      return;
    }

    setForgotLoading(true);
    try {
      await sendPasswordResetEmail(auth, forgotEmail);
      setShowForgot(false);
      setForgotEmail('');
      setShowSuccess(true);
    } catch (err) {
      console.error('Forgot error:', err);
      if (err.code === 'auth/user-not-found') {
        setForgotError('Is email se koi account nahi mila.');
      } else {
        setForgotError('Email bhejne mein masla aaya.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-6 md:py-10 anim-fadeIn"
      style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">

        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold btn-2d mb-6"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

          {/* Left - Branding */}
          <div className="hidden lg:block anim-fadeInLeft">
            <div className="rounded-3xl p-10 shadow-xl relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #F5AEC0 0%, #075B91 100%)', color: '#fff' }}>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white opacity-10 -translate-y-1/2 translate-x-1/2"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white opacity-10 translate-y-1/2 -translate-x-1/2"></div>

              <div className="relative z-10">
                {siteLogo ? (
                  <img src={siteLogo} alt={siteName} className="h-20 w-auto max-w-[200px] object-contain mb-6 rounded" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center mb-6">
                    <Leaf size={32} />
                  </div>
                )}
                <h1 className="text-4xl font-bold mb-4 leading-tight">
                  Welcome Back to<br />{siteName}
                </h1>
                <p className="text-white/90 mb-8 leading-relaxed">
                  Login karein aur apne orders track karein, wishlist dekhein, aur exclusive deals payein.
                </p>

                <div className="space-y-4">
                  {[
                    'Track Your Orders',
                    'Faster Checkout',
                    'Saved Addresses',
                    'Personalized Deals',
                  ].map((benefit, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                        <CheckCircle size={16} />
                      </div>
                      <span className="text-sm font-semibold">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="anim-fadeInRight">
            <div className="rounded-3xl p-6 md:p-8 shadow-xl"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>

              {/* Logo/Name */}
              <div className="text-center mb-6">
                {siteLogo ? (
                  <img src={siteLogo} alt={siteName} className="h-16 w-auto max-w-[180px] object-contain mx-auto mb-3" />
                ) : (
                  <div className="inline-flex items-center gap-2 mb-3">
                    <Leaf size={28} style={{ color: 'var(--color-link)' }} />
                    <span className="text-xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
                      {siteName}
                    </span>
                  </div>
                )}
                <h2 className="text-2xl md:text-3xl font-bold mb-1" style={{ color: 'var(--color-primary-text)' }}>
                  Welcome Back
                </h2>
                <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                  Login to continue shopping
                </p>
              </div>

              {error && (
                <div className="text-sm p-3 rounded-lg mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 anim-shake">
                  <AlertCircle size={16} /> {error}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">

                <div>
                  <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-primary-text)' }}>
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input
                      type="email" name="email" value={formData.email} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="ahmed@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowForgot(true)}
                      className="text-xs font-semibold hover:underline"
                      style={{ color: 'var(--color-link)' }}
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                    <input
                      type={showPassword ? 'text' : 'password'} name="password"
                      value={formData.password} onChange={handleChange}
                      className="w-full rounded-lg py-3 pl-11 pr-11 text-sm focus:outline-none"
                      style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                      placeholder="••••••••"
                      required
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3.5" style={{ color: 'var(--color-secondary-text)' }}>
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-full py-3.5 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg ripple-btn ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
                >
                  {loading ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                        style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>

                <div className="flex items-center gap-3 my-3">
                  <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }}></div>
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-secondary-text)' }}>OR</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }}></div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={googleLoading}
                  className={`w-full rounded-full py-3.5 font-semibold flex items-center justify-center gap-3 btn-2d transition ${googleLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                  style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
                >
                  {googleLoading ? (
                    <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                      style={{ borderColor: 'var(--color-primary-text)', borderTopColor: 'transparent' }} />
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>

                <p className="text-center text-sm mt-4" style={{ color: 'var(--color-secondary-text)' }}>
                  Account nahi hai?{' '}
                  <Link to="/register" className="font-semibold underline" style={{ color: 'var(--color-link)' }}>
                    Register karein
                  </Link>
                </p>

              </form>
            </div>
          </div>
        </div>

      </div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 anim-fadeIn"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowForgot(false)}>
          <div
            className="rounded-2xl shadow-2xl max-w-md w-full p-6 anim-popIn relative"
            style={{ backgroundColor: 'var(--color-card-bg)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setShowForgot(false)}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 transition"
              style={{ color: 'var(--color-secondary-text)' }}>
              <X size={18} />
            </button>

            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'rgba(7, 91, 145, 0.12)' }}>
                <Mail size={32} style={{ color: '#075B91' }} />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center mb-2" style={{ color: 'var(--color-primary-text)' }}>
              Reset Password
            </h2>
            <p className="text-xs text-center mb-4" style={{ color: 'var(--color-secondary-text)' }}>
              Apna email daalein, hum aapko password reset link bhejenge.
            </p>

            {forgotError && (
              <div className="text-sm p-3 rounded-lg mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200">
                <AlertCircle size={16} /> {forgotError}
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-3.5" size={18} style={{ color: 'var(--color-secondary-text)' }} />
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="w-full rounded-lg py-3 pl-11 pr-4 text-sm focus:outline-none"
                  style={{ border: `1px solid var(--color-border)`, backgroundColor: 'var(--color-card-bg)', color: 'var(--color-primary-text)' }}
                  placeholder="ahmed@example.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={forgotLoading}
                className={`w-full rounded-full py-3 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg ${forgotLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
              >
                {forgotLoading ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-t-transparent rounded-full anim-spin"
                      style={{ borderColor: 'currentColor', borderTopColor: 'transparent' }} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={16} /> Send Reset Link
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Email Sent!"
        message={`Password reset link ${forgotEmail} par bhej diya gaya hai. Inbox check karein.`}
        buttonText="OK"
      />

    </div>
  );
};

export default Login;